import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  Assessment as AssessmentIcon,
  Calculate as CalculateIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

interface EstimateOptimizationProps {
  filterState: any;
  onFilterChange: any;
}

export interface EstimateOptimizationRef {
  refresh: () => void;
}

interface JobCodeData {
  jobCode: string;
  totalCompletes: number;
  totalD2CCompletes: number;
  estimateDeclines: number;
  acceptanceRate: number;
  avgEstimatePrice: number;
  avgRepairProfit: number;
  avgRepairRevenue: number;
  avgTripProfit: number;
  totalProfit: number;
  recommendedAppliance?: string;
  appliancePrice?: number;
  repairVsReplaceRatio?: number;
}

interface SimulationResult {
  scenario: string;
  description: string;
  totalProfit: number;
  profitDelta: number;
  profitDeltaPct: number;
  acceptanceRate: number;
  acceptanceRateDelta: number;
  avgDiscount: number;
  additionalRepairs: number;
}

interface PriceBucket {
  priceBucket: string;
  bucketOrder: number;
  // totalOrders: number;
  totalD2CCompletes: number;
  estimateDeclines: number;
  acceptanceRate: number;
  avgQuotePrice: number;
  avgRepairRevenue: number;
  avgRepairProfit: number;
  // avgRepairCost: number;
  avgTripProfit: number;
}

interface BucketSimulation {
  priceBucket: string;
  bucketOrder: number;
  // totalOrders: number;
  totalD2CCompletes: number;
  estimateDeclines: number;
  acceptanceRate: number;
  avgQuotePrice: number;
  avgRepairRevenue: number;
  avgRepairProfit: number;
  avgTripProfit: number;
  scenarios: SimulationResult[];
}

const EstimateOptimization = forwardRef<
  EstimateOptimizationRef,
  EstimateOptimizationProps
>(({ filterState, onFilterChange }, ref) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [topJobCodes, setTopJobCodes] = useState<JobCodeData[]>([]);
  const [allJobCodes, setAllJobCodes] = useState<JobCodeData[]>([]);
  const [selectedJobCode, setSelectedJobCode] = useState<string>("");
  const [selectedAppliance, setSelectedAppliance] = useState<string>("All");
  const [selectedJobCodeData, setSelectedJobCodeData] =
    useState<JobCodeData | null>(null);
  const [jobCodeSummary, setJobCodeSummary] = useState<any>(null);
  const [simulationResults, setSimulationResults] = useState<
    SimulationResult[]
  >([]);
  const [priceBuckets, setPriceBuckets] = useState<PriceBucket[]>([]);
  const [bucketSimulations, setBucketSimulations] = useState<
    BucketSimulation[]
  >([]);
  const [priceElasticity, setPriceElasticity] = useState<number>(-1.5);
  const [simulationLoading, setSimulationLoading] = useState(false);
  const [calculationDialogOpen, setCalculationDialogOpen] = useState(false);
  const [selectedScenario, setSelectedScenario] =
    useState<SimulationResult | null>(null);
  const [selectedBucket, setSelectedBucket] =
    useState<BucketSimulation | null>(null);
  const [showBucketAnalysis, setShowBucketAnalysis] = useState(false);

  // Fetch top 200 job codes
  const fetchTopJobCodes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch("/api/snowflake/top-job-codes", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (data.success && data.jobCodes) {
        setAllJobCodes(data.jobCodes);
        setTopJobCodes(data.jobCodes);
        // Set default to top job code
        if (data.jobCodes.length > 0) {
          setSelectedJobCode(
            data.jobCodes[0].JOBCODE || data.jobCodes[0].jobCode
          );
        }
      } else {
        setError("Failed to fetch job codes");
      }
    } catch (err: any) {
      setError("Failed to fetch data");
      console.error("Error fetching top job codes:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch job code summary
  const fetchJobCodeSummary = useCallback(async () => {
    if (!selectedJobCode) return;

    try {
      const response = await fetch("/api/snowflake/job-code-summary", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          jobCode: selectedJobCode,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setJobCodeSummary(data.summary);
      }
    } catch (err: any) {
      console.error("Error fetching job code summary:", err);
    }
  }, [selectedJobCode]);

  // Fetch simulation data for selected job code
  const fetchSimulation = useCallback(async () => {
    if (!selectedJobCode) return;

    try {
      setSimulationLoading(true);
      setError(null);

      const response = await fetch("/api/snowflake/simulate-job-code", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          jobCode: selectedJobCode,
          priceElasticity: priceElasticity,
        }),
      });

      const data = await response.json();

      if (data.success) {
        console.log("Simulation data received:", {
          baseline: data.baseline,
          scenarios: data.scenarios?.length,
          priceBuckets: data.priceBuckets?.length,
          bucketSimulations: data.bucketSimulations?.length,
          bucketSimulationsData: data.bucketSimulations,
        });
        setSelectedJobCodeData(data.baseline);
        setSimulationResults(data.scenarios);
        setPriceBuckets(data.priceBuckets || []);
        setBucketSimulations(data.bucketSimulations || []);
      } else {
        setError("Failed to run simulation");
      }
    } catch (err: any) {
      setError("Failed to run simulation");
      console.error("Error running simulation:", err);
    } finally {
      setSimulationLoading(false);
    }
  }, [selectedJobCode, priceElasticity]);

  // Filter job codes by appliance type
  useEffect(() => {
    if (selectedAppliance === "All") {
      setTopJobCodes(allJobCodes);
    } else {
      const filtered = allJobCodes.filter(
        (jc: any) =>
          (jc.APPLIANCETYPE || jc.applianceType) === selectedAppliance
      );
      setTopJobCodes(filtered);
      // Update selected job code if current one is not in filtered list
      if (
        filtered.length > 0 &&
        !filtered.find(
          (jc: any) => (jc.JOBCODE || jc.jobCode) === selectedJobCode
        )
      ) {
        setSelectedJobCode((filtered[0] as any).JOBCODE || filtered[0].jobCode);
      }
    }
  }, [selectedAppliance, allJobCodes, selectedJobCode]);

  useEffect(() => {
    fetchTopJobCodes();
  }, [fetchTopJobCodes]);

  useEffect(() => {
    if (selectedJobCode) {
      fetchSimulation();
      fetchJobCodeSummary();
    }
  }, [selectedJobCode, priceElasticity, fetchSimulation, fetchJobCodeSummary]);

  useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        fetchTopJobCodes();
        if (selectedJobCode) {
          fetchSimulation();
        }
      },
    }),
    [fetchTopJobCodes, fetchSimulation, selectedJobCode]
  );

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  const getBestScenario = () => {
    if (simulationResults.length === 0) return null;
    return simulationResults.reduce((best, current) =>
      current.profitDelta > best.profitDelta ? current : best
    );
  };

  const getUniqueAppliances = () => {
    const appliances = allJobCodes
      .map((jc: any) => jc.APPLIANCETYPE || jc.applianceType)
      .filter(Boolean);
    return ["All", ...Array.from(new Set(appliances))].sort();
  };

  const bestScenario = getBestScenario();

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Estimate Decline Optimization
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Analyze pricing elasticity and simulate counter-offer strategies to
          optimize profit and acceptance rates
        </Typography>
      </Box>

      {/* Controls Section */}
      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Appliance Type</InputLabel>
            <Select
              value={selectedAppliance}
              label="Appliance Type"
              onChange={(e) => setSelectedAppliance(e.target.value)}
            >
              {getUniqueAppliances().map((appliance) => (
                <MenuItem key={appliance} value={appliance}>
                  {appliance}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Select Job Code</InputLabel>
            <Select
              value={selectedJobCode}
              label="Select Job Code"
              onChange={(e) => setSelectedJobCode(e.target.value)}
            >
              {topJobCodes.map((jc: any) => (
                <MenuItem
                  key={jc.JOBCODE || jc.jobCode}
                  value={jc.JOBCODE || jc.jobCode}
                >
                  {jc.JOBCODE || jc.jobCode} -{" "}
                  {(
                    jc.TOTALCOMPLETES ||
                    jc.totalCompletes ||
                    0
                  ).toLocaleString()}{" "}
                  completes
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              p: 1.25,
              pt: 0.75,
              pb: 0.5,
              height: "56px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{ fontWeight: 500, fontSize: "0.8rem", lineHeight: 1.2 }}
            >
              Price Elasticity: {priceElasticity.toFixed(2)}
            </Typography>
            <Slider
              value={priceElasticity}
              onChange={(_, value) => setPriceElasticity(value as number)}
              min={-3.0}
              max={-0.5}
              step={0.1}
              marks={[
                { value: -3.0, label: "-3.0" },
                { value: -2.0, label: "-2.0" },
                { value: -1.0, label: "-1.0" },
                { value: -0.5, label: "-0.5" },
              ]}
              valueLabelDisplay="auto"
              size="small"
              sx={{ my: 0.25 }}
            />
          </Box>
        </Grid>
      </Grid>

      {simulationLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Baseline Metrics */}
          {selectedJobCodeData && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: 700, color: "#424242" }}
              >
                Current State - {selectedJobCode}
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 0.75,
                      bgcolor: "#e3f2fd",
                      border: "1px solid #90caf9",
                      borderRadius: 1,
                      textAlign: "center",
                      height: "50px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#1565c0",
                        fontWeight: 600,
                        fontSize: "0.6rem",
                      }}
                    >
                      Total Completes
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: "#0d47a1",
                        mt: 0.2,
                        fontSize: "0.875rem",
                      }}
                    >
                      {(
                        selectedJobCodeData.totalCompletes || 0
                      ).toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 0.75,
                      bgcolor: "#e3f2fd",
                      border: "1px solid #90caf9",
                      borderRadius: 1,
                      textAlign: "center",
                      height: "50px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#1565c0",
                        fontWeight: 600,
                        fontSize: "0.6rem",
                      }}
                    >
                      Estimate Declines
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: "#0d47a1",
                        mt: 0.2,
                        fontSize: "0.875rem",
                      }}
                    >
                      {(
                        selectedJobCodeData.estimateDeclines || 0
                      ).toLocaleString()}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#1565c0", fontSize: "0.6rem" }}
                    >
                      {formatPercent(selectedJobCodeData.acceptanceRate || 0)}{" "}
                      acceptance
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 0.75,
                      bgcolor: "#e3f2fd",
                      border: "1px solid #90caf9",
                      borderRadius: 1,
                      textAlign: "center",
                      height: "50px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#1565c0",
                        fontWeight: 600,
                        fontSize: "0.6rem",
                      }}
                    >
                      Avg Estimate Price
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: "#0d47a1",
                        mt: 0.2,
                        fontSize: "0.875rem",
                      }}
                    >
                      {formatCurrency(
                        selectedJobCodeData.avgEstimatePrice || 0
                      )}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 0.75,
                      bgcolor: "#e3f2fd",
                      border: "1px solid #90caf9",
                      borderRadius: 1,
                      textAlign: "center",
                      height: "50px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#1565c0",
                        fontWeight: 600,
                        fontSize: "0.6rem",
                      }}
                    >
                      Total Profit
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: "#0d47a1",
                        mt: 0.2,
                        fontSize: "0.875rem",
                      }}
                    >
                      {formatCurrency(selectedJobCodeData.totalProfit || 0)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Job Code Summary Section */}
          {jobCodeSummary && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                📊 Job Code Summary
              </Typography>
              <Card
                sx={{ backgroundColor: "#1a237e", border: "2px solid #3949ab" }}
              >
                <CardContent sx={{ p: 1.5 }}>
                  {/* Volume Metrics */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: "#90caf9",
                        mb: 1,
                        borderBottom: "1px solid #3949ab",
                        pb: 0.5,
                      }}
                    >
                      📈 Volume Metrics
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6} md={3}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 0.75,
                            bgcolor: "#283593",
                            borderRadius: 1,
                            height: "50px",
                          }}
                        >
                          <Box sx={{ textAlign: "center", flex: 1 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#b0bec5",
                                fontWeight: 600,
                                fontSize: "0.6rem",
                              }}
                            >
                              Total Completes
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: "#fff",
                                mt: 0.2,
                                fontSize: "0.875rem",
                              }}
                            >
                              {(
                                jobCodeSummary["Total Completes"] || 0
                              ).toLocaleString()}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 0.75,
                            bgcolor: "#283593",
                            borderRadius: 1,
                            height: "50px",
                          }}
                        >
                          <Box sx={{ textAlign: "center", flex: 1 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#b0bec5",
                                fontWeight: 600,
                                fontSize: "0.6rem",
                              }}
                            >
                              D2C Completes
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: "#fff",
                                mt: 0.2,
                                fontSize: "0.875rem",
                              }}
                            >
                              {(
                                jobCodeSummary["Total D2C Completes"] || 0
                              ).toLocaleString()}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#81c784",
                              fontWeight: 700,
                              fontSize: "0.65rem",
                              ml: 1,
                            }}
                          >
                            {(jobCodeSummary["D2C Completes %"] || 0).toFixed(
                              1
                            )}
                            %
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 0.75,
                            bgcolor: "#283593",
                            borderRadius: 1,
                            height: "50px",
                          }}
                        >
                          <Box sx={{ textAlign: "center", flex: 1 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#b0bec5",
                                fontWeight: 600,
                                fontSize: "0.6rem",
                              }}
                            >
                              B2B Completes
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: "#fff",
                                mt: 0.2,
                                fontSize: "0.875rem",
                              }}
                            >
                              {(
                                jobCodeSummary["Total B2B Completes"] || 0
                              ).toLocaleString()}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#64b5f6",
                              fontWeight: 700,
                              fontSize: "0.65rem",
                              ml: 1,
                            }}
                          >
                            {(jobCodeSummary["B2B Completes %"] || 0).toFixed(
                              1
                            )}
                            %
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 0.75,
                            bgcolor: "#283593",
                            borderRadius: 1,
                            height: "50px",
                          }}
                        >
                          <Box sx={{ textAlign: "center", flex: 1 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#b0bec5",
                                fontWeight: 600,
                                fontSize: "0.6rem",
                              }}
                            >
                              Estimate Declines
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: "#fff",
                                mt: 0.2,
                                fontSize: "0.875rem",
                              }}
                            >
                              {(
                                jobCodeSummary["Estimate Declines"] || 0
                              ).toLocaleString()}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#e57373",
                              fontWeight: 700,
                              fontSize: "0.65rem",
                              ml: 1,
                            }}
                          >
                            {(
                              jobCodeSummary["Estimate Decline %"] || 0
                            ).toFixed(1)}
                            %
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Pricing & Financial Metrics Combined */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: "#90caf9",
                        mb: 1,
                        borderBottom: "1px solid #3949ab",
                        pb: 0.5,
                      }}
                    >
                      💰 Pricing & Financial Metrics
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={4} md={2}>
                        <Box
                          sx={{
                            p: 0.75,
                            bgcolor: "#283593",
                            borderRadius: 1,
                            textAlign: "center",
                            minHeight: "50px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#b0bec5",
                              fontWeight: 600,
                              fontSize: "0.6rem",
                            }}
                          >
                            Total Quoted
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: "#fff",
                              mt: 0.2,
                              fontSize: "0.875rem",
                            }}
                          >
                            {formatCurrency(
                              jobCodeSummary["Total Quoted Per Call"] || 0
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4} md={2}>
                        <Box
                          sx={{
                            p: 0.75,
                            bgcolor: "#283593",
                            borderRadius: 1,
                            textAlign: "center",
                            minHeight: "50px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#b0bec5",
                              fontWeight: 600,
                              fontSize: "0.6rem",
                            }}
                          >
                            Labor Quoted
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: "#fff",
                              mt: 0.2,
                              fontSize: "0.875rem",
                            }}
                          >
                            {formatCurrency(
                              jobCodeSummary["Labor Quoted Per Call"] || 0
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4} md={2}>
                        <Box
                          sx={{
                            p: 0.75,
                            bgcolor: "#283593",
                            borderRadius: 1,
                            textAlign: "center",
                            minHeight: "50px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#b0bec5",
                              fontWeight: 600,
                              fontSize: "0.6rem",
                            }}
                          >
                            Parts Quoted
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: "#fff",
                              mt: 0.2,
                              fontSize: "0.875rem",
                            }}
                          >
                            {formatCurrency(
                              jobCodeSummary["Parts Quoted Per Call"] || 0
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4} md={2}>
                        <Box
                          sx={{
                            p: 0.75,
                            bgcolor: "#283593",
                            borderRadius: 1,
                            textAlign: "center",
                            minHeight: "50px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#b0bec5",
                              fontWeight: 600,
                              fontSize: "0.6rem",
                            }}
                          >
                            D2C Est Revenue
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: "#64b5f6",
                              mt: 0.2,
                              fontSize: "0.875rem",
                            }}
                          >
                            {formatCurrency(
                              jobCodeSummary["D2C Est Revenue"] || 0
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4} md={2}>
                        <Box
                          sx={{
                            p: 0.75,
                            bgcolor: "#283593",
                            borderRadius: 1,
                            textAlign: "center",
                            minHeight: "50px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#b0bec5",
                              fontWeight: 600,
                              fontSize: "0.6rem",
                            }}
                          >
                            D2C Est Profit
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: "#81c784",
                              mt: 0.2,
                              fontSize: "0.875rem",
                            }}
                          >
                            {formatCurrency(
                              jobCodeSummary["D2C Est Profit"] || 0
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4} md={2}>
                        <Box
                          sx={{
                            p: 0.75,
                            bgcolor: "#283593",
                            borderRadius: 1,
                            textAlign: "center",
                            minHeight: "50px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#b0bec5",
                              fontWeight: 600,
                              fontSize: "0.6rem",
                            }}
                          >
                            D2C Est Cost
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: "#ffb74d",
                              mt: 0.2,
                              fontSize: "0.875rem",
                            }}
                          >
                            {formatCurrency(
                              jobCodeSummary["D2C Est Cost"] || 0
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Replacement Analysis */}
                  {jobCodeSummary["Recommended Appliance"] && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          color: "#90caf9",
                          mb: 1,
                          borderBottom: "1px solid #3949ab",
                          pb: 0.5,
                        }}
                      >
                        🔄 Repair vs Replace Analysis
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12} md={4}>
                          <Box
                            sx={{
                              p: 0.75,
                              bgcolor: "#283593",
                              borderRadius: 1,
                              textAlign: "center",
                              minHeight: "50px",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#b0bec5",
                                fontWeight: 600,
                                fontSize: "0.6rem",
                              }}
                            >
                              Recommended Appliance
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: "#fff",
                                mt: 0.2,
                                fontSize: "0.75rem",
                              }}
                            >
                              {jobCodeSummary["Recommended Appliance"]}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} md={4}>
                          <Box
                            sx={{
                              p: 0.75,
                              bgcolor: "#283593",
                              borderRadius: 1,
                              textAlign: "center",
                              minHeight: "50px",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#b0bec5",
                                fontWeight: 600,
                                fontSize: "0.6rem",
                              }}
                            >
                              Replacement Cost
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: "#64b5f6",
                                mt: 0.2,
                                fontSize: "0.875rem",
                              }}
                            >
                              {formatCurrency(
                                jobCodeSummary["Appliance Price"] || 0
                              )}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} md={4}>
                          <Box
                            sx={{
                              p: 0.75,
                              bgcolor: "#283593",
                              borderRadius: 1,
                              textAlign: "center",
                              minHeight: "50px",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#b0bec5",
                                fontWeight: 600,
                                fontSize: "0.6rem",
                              }}
                            >
                              Repair vs Replace
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color:
                                  (jobCodeSummary["Repair vs Replace Ratio"] ||
                                    0) < 50
                                    ? "#81c784"
                                    : (jobCodeSummary[
                                        "Repair vs Replace Ratio"
                                      ] || 0) < 80
                                    ? "#ffb74d"
                                    : "#e57373",
                                mt: 0.2,
                                fontSize: "0.875rem",
                              }}
                            >
                              {jobCodeSummary["Repair vs Replace Ratio"] || 0}%
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {/* Quality Indicators */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: "#90caf9",
                        mb: 1,
                        borderBottom: "1px solid #3949ab",
                        pb: 0.5,
                      }}
                    >
                      ⚠️ Quality Indicators
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6} md={3}>
                        <Box
                          sx={{
                            p: 0.75,
                            bgcolor: "#283593",
                            borderRadius: 1,
                            textAlign: "center",
                            minHeight: "50px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#b0bec5",
                              fontWeight: 600,
                              fontSize: "0.6rem",
                            }}
                          >
                            Repair &gt; MSRP
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: "#ffb74d",
                              mt: 0.2,
                              fontSize: "0.875rem",
                            }}
                          >
                            {(
                              jobCodeSummary["Repair Higher Than MSRP"] || 0
                            ).toLocaleString()}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box
                          sx={{
                            p: 0.75,
                            bgcolor: "#283593",
                            borderRadius: 1,
                            textAlign: "center",
                            minHeight: "50px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#b0bec5",
                              fontWeight: 600,
                              fontSize: "0.6rem",
                            }}
                          >
                            Time &lt; 20 Min
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: "#81c784",
                              mt: 0.2,
                              fontSize: "0.875rem",
                            }}
                          >
                            {(
                              jobCodeSummary["Repair Time LT 20 Mins"] || 0
                            ).toLocaleString()}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box
                          sx={{
                            p: 0.75,
                            bgcolor: "#283593",
                            borderRadius: 1,
                            textAlign: "center",
                            minHeight: "50px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#b0bec5",
                              fontWeight: 600,
                              fontSize: "0.6rem",
                            }}
                          >
                            Time &gt; 60 Min
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: "#e57373",
                              mt: 0.2,
                              fontSize: "0.875rem",
                            }}
                          >
                            {(
                              jobCodeSummary["Repair Time GT 60 Mins"] || 0
                            ).toLocaleString()}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box
                          sx={{
                            p: 0.75,
                            bgcolor: "#283593",
                            borderRadius: 1,
                            textAlign: "center",
                            minHeight: "50px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#b0bec5",
                              fontWeight: 600,
                              fontSize: "0.6rem",
                            }}
                          >
                            No Deductible
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: "#ffb74d",
                              mt: 0.2,
                              fontSize: "0.875rem",
                            }}
                          >
                            {(
                              jobCodeSummary["Deductible Not Collected"] || 0
                            ).toLocaleString()}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Best Scenario Highlight */}
          {bestScenario && (
            <Box sx={{ mb: 3 }}>
              <Card
                sx={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        🎯 Recommended Strategy
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                        {bestScenario.scenario}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {bestScenario.description}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Projected Profit Lift
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {formatCurrency(bestScenario.profitDelta || 0)}
                      </Typography>
                      <Typography variant="h6">
                        +{formatPercent(bestScenario.profitDeltaPct || 0)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Simulation Results Table */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Scenario Comparison
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: 600 }}>Scenario</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Total Profit
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Profit Δ
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Profit Δ %
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Acceptance Rate
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Rate Δ
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Avg Discount
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Additional Repairs
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Details
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {simulationResults.map((result, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor:
                          result === bestScenario
                            ? "rgba(102, 126, 234, 0.1)"
                            : "inherit",
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {result.scenario}
                          {result === bestScenario && (
                            <Chip
                              label="Best"
                              size="small"
                              color="primary"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{result.description}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(result.totalProfit || 0)}
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            color:
                              (result.profitDelta || 0) > 0
                                ? "success.main"
                                : (result.profitDelta || 0) < 0
                                ? "error.main"
                                : "text.primary",
                          }}
                        >
                          {(result.profitDelta || 0) > 0 ? (
                            <TrendingUpIcon fontSize="small" sx={{ mr: 0.5 }} />
                          ) : (result.profitDelta || 0) < 0 ? (
                            <TrendingDownIcon
                              fontSize="small"
                              sx={{ mr: 0.5 }}
                            />
                          ) : null}
                          {formatCurrency(Math.abs(result.profitDelta || 0))}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          sx={{
                            color:
                              (result.profitDeltaPct || 0) > 0
                                ? "success.main"
                                : (result.profitDeltaPct || 0) < 0
                                ? "error.main"
                                : "text.primary",
                            fontWeight: 600,
                          }}
                        >
                          {(result.profitDeltaPct || 0) > 0 ? "+" : ""}
                          {formatPercent(result.profitDeltaPct || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatPercent(result.acceptanceRate || 0)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          sx={{
                            color:
                              (result.acceptanceRateDelta || 0) > 0
                                ? "success.main"
                                : (result.acceptanceRateDelta || 0) < 0
                                ? "error.main"
                                : "text.primary",
                          }}
                        >
                          {(result.acceptanceRateDelta || 0) > 0 ? "+" : ""}
                          {formatPercent(result.acceptanceRateDelta || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatPercent(result.avgDiscount || 0)}
                      </TableCell>
                      <TableCell align="right">
                        {(result.additionalRepairs || 0).toLocaleString()}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setSelectedScenario(result);
                            setCalculationDialogOpen(true);
                          }}
                        >
                          <CalculateIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Price Range Analysis - By Quote Range */}
          <Box sx={{ mt: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                📊 Price Sensitivity by Quote Range
              </Typography>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ mr: 2, color: "text.secondary" }}
                >
                  {bucketSimulations.length} ranges found
                </Typography>
                <Button
                  variant={showBucketAnalysis ? "contained" : "outlined"}
                  size="small"
                  onClick={() => setShowBucketAnalysis(!showBucketAnalysis)}
                  disabled={bucketSimulations.length === 0}
                >
                  {showBucketAnalysis ? "Hide" : "Show"} Quote Range Analysis
                </Button>
              </Box>
            </Box>

            {bucketSimulations.length === 0 ? (
              <Card sx={{ p: 2, bgcolor: "#f5f5f5" }}>
                <Typography variant="body2" color="text.secondary">
                  No price bucket data available. This could be because:
                  <ul>
                    <li>
                      The job code has no estimate decline data with valid
                      prices
                    </li>
                    <li>The data is still loading</li>
                    <li>
                      There was an error fetching the bucket data (check
                      console)
                    </li>
                  </ul>
                </Typography>
              </Card>
            ) : (
              showBucketAnalysis && (
                <Box>
                  {bucketSimulations.map((bucket, bucketIndex) => (
                    <Card
                      key={bucket.priceBucket}
                      sx={{ mb: 3, border: "1px solid #e0e0e0" }}
                    >
                      <CardContent>
                        {/* Quote Range Header */}
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 700, mb: 2, color: "#1976d2" }}
                        >
                          Quote Range: {bucket.priceBucket}
                        </Typography>

                        {/* Quote Range Summary - Similar to Job Code Summary */}
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, fontWeight: 700, color: "#424242" }}
                          >
                            Range Summary
                          </Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={6} sm={4} md={2}>
                              <Box
                                sx={{
                                  p: 0.75,
                                  bgcolor: "#e3f2fd",
                                  border: "1px solid #90caf9",
                                  borderRadius: 1,
                                  textAlign: "center",
                                  height: "60px",
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#1565c0",
                                    fontWeight: 600,
                                    fontSize: "0.6rem",
                                  }}
                                >
                                  Total D2C Orders
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    color: "#0d47a1",
                                    mt: 0.2,
                                    fontSize: "0.875rem",
                                  }}
                                >
                                  {(
                                    bucket.totalD2CCompletes || 0
                                  ).toLocaleString()}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                              <Box
                                sx={{
                                  p: 0.75,
                                  bgcolor: "#ffebee",
                                  border: "1px solid #ef9a9a",
                                  borderRadius: 1,
                                  textAlign: "center",
                                  height: "60px",
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#c62828",
                                    fontWeight: 600,
                                    fontSize: "0.6rem",
                                  }}
                                >
                                  Estimate Declines
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    color: "#b71c1c",
                                    mt: 0.2,
                                    fontSize: "0.875rem",
                                  }}
                                >
                                  {(
                                    bucket.estimateDeclines || 0
                                  ).toLocaleString()}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                              <Box
                                sx={{
                                  p: 0.75,
                                  bgcolor: "#e8f5e9",
                                  border: "1px solid #81c784",
                                  borderRadius: 1,
                                  textAlign: "center",
                                  height: "60px",
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#2e7d32",
                                    fontWeight: 600,
                                    fontSize: "0.6rem",
                                  }}
                                >
                                  Accepted Repairs
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    color: "#1b5e20",
                                    mt: 0.2,
                                    fontSize: "0.875rem",
                                  }}
                                >
                                  {(
                                    (bucket.totalD2CCompletes || 0) -
                                    (bucket.estimateDeclines || 0)
                                  ).toLocaleString()}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                              <Box
                                sx={{
                                  p: 0.75,
                                  bgcolor: "#e3f2fd",
                                  border: "1px solid #90caf9",
                                  borderRadius: 1,
                                  textAlign: "center",
                                  height: "60px",
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#1565c0",
                                    fontWeight: 600,
                                    fontSize: "0.6rem",
                                  }}
                                >
                                  Acceptance Rate
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    color:
                                      (bucket.acceptanceRate || 0) > 50
                                        ? "#2e7d32"
                                        : (bucket.acceptanceRate || 0) > 30
                                        ? "#ed6c02"
                                        : "#d32f2f",
                                    mt: 0.2,
                                    fontSize: "0.875rem",
                                  }}
                                >
                                  {formatPercent(bucket.acceptanceRate || 0)}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                              <Box
                                sx={{
                                  p: 0.75,
                                  bgcolor: "#e3f2fd",
                                  border: "1px solid #90caf9",
                                  borderRadius: 1,
                                  textAlign: "center",
                                  height: "60px",
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#1565c0",
                                    fontWeight: 600,
                                    fontSize: "0.6rem",
                                  }}
                                >
                                  Avg D2C Revenue
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    color: "#0d47a1",
                                    mt: 0.2,
                                    fontSize: "0.875rem",
                                  }}
                                >
                                  {formatCurrency(bucket.avgQuotePrice || 0)}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                              <Box
                                sx={{
                                  p: 0.75,
                                  bgcolor: "#e3f2fd",
                                  border: "1px solid #90caf9",
                                  borderRadius: 1,
                                  textAlign: "center",
                                  height: "60px",
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#1565c0",
                                    fontWeight: 600,
                                    fontSize: "0.6rem",
                                  }}
                                >
                                  Avg Repair Profit
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    color: "#0d47a1",
                                    mt: 0.2,
                                    fontSize: "0.875rem",
                                  }}
                                >
                                  {formatCurrency(bucket.avgRepairProfit || 0)}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                              <Box
                                sx={{
                                  p: 0.75,
                                  bgcolor: "#e3f2fd",
                                  border: "1px solid #90caf9",
                                  borderRadius: 1,
                                  textAlign: "center",
                                  height: "60px",
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#1565c0",
                                    fontWeight: 600,
                                    fontSize: "0.6rem",
                                  }}
                                >
                                  Avg Trip Profit
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    color: "#0d47a1",
                                    mt: 0.2,
                                    fontSize: "0.875rem",
                                  }}
                                >
                                  {formatCurrency(bucket.avgTripProfit || 0)}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Scenario Comparison for this Quote Range */}
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1.5, fontWeight: 700, color: "#424242" }}
                        >
                          Scenario Comparison for {bucket.priceBucket}
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                                <TableCell
                                  sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                                >
                                  Scenario
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                                >
                                  Total Profit
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                                >
                                  Profit Δ
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                                >
                                  Profit Δ %
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                                >
                                  Accept Rate
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                                >
                                  Rate Δ
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                                >
                                  Avg Discount
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                                >
                                  Additional Repairs
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                                >
                                  Details
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {bucket.scenarios.map(
                                (scenario, scenarioIndex) => {
                                  const isBest =
                                    bucket.scenarios.reduce((best, current) =>
                                      (current.profitDelta || 0) >
                                      (best.profitDelta || 0)
                                        ? current
                                        : best
                                    ) === scenario;

                                  return (
                                    <TableRow
                                      key={scenarioIndex}
                                      sx={{
                                        backgroundColor: isBest
                                          ? "rgba(102, 126, 234, 0.1)"
                                          : "inherit",
                                        "&:hover": {
                                          backgroundColor:
                                            "rgba(0, 0, 0, 0.04)",
                                        },
                                      }}
                                    >
                                      <TableCell>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            sx={{ fontSize: "0.75rem" }}
                                          >
                                            {scenario.scenario}
                                          </Typography>
                                          {isBest && (
                                            <Chip
                                              label="Best"
                                              size="small"
                                              color="primary"
                                              sx={{
                                                ml: 1,
                                                height: "18px",
                                                fontSize: "0.65rem",
                                              }}
                                            />
                                          )}
                                        </Box>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography
                                          variant="body2"
                                          sx={{ fontSize: "0.75rem" }}
                                        >
                                          {formatCurrency(
                                            scenario.totalProfit || 0
                                          )}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontSize: "0.75rem",
                                            color:
                                              (scenario.profitDelta || 0) > 0
                                                ? "success.main"
                                                : (scenario.profitDelta || 0) <
                                                  0
                                                ? "error.main"
                                                : "text.primary",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {(scenario.profitDelta || 0) > 0
                                            ? "+"
                                            : ""}
                                          {formatCurrency(
                                            scenario.profitDelta || 0
                                          )}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontSize: "0.75rem",
                                            color:
                                              (scenario.profitDeltaPct || 0) > 0
                                                ? "success.main"
                                                : (scenario.profitDeltaPct ||
                                                    0) < 0
                                                ? "error.main"
                                                : "text.primary",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {(scenario.profitDeltaPct || 0) > 0
                                            ? "+"
                                            : ""}
                                          {formatPercent(
                                            scenario.profitDeltaPct || 0
                                          )}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography
                                          variant="body2"
                                          sx={{ fontSize: "0.75rem" }}
                                        >
                                          {formatPercent(
                                            scenario.acceptanceRate || 0
                                          )}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontSize: "0.75rem",
                                            color:
                                              (scenario.acceptanceRateDelta ||
                                                0) > 0
                                                ? "success.main"
                                                : (scenario.acceptanceRateDelta ||
                                                    0) < 0
                                                ? "error.main"
                                                : "text.primary",
                                          }}
                                        >
                                          {(scenario.acceptanceRateDelta || 0) >
                                          0
                                            ? "+"
                                            : ""}
                                          {formatPercent(
                                            scenario.acceptanceRateDelta || 0
                                          )}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography
                                          variant="body2"
                                          sx={{ fontSize: "0.75rem" }}
                                        >
                                          {formatPercent(
                                            scenario.avgDiscount || 0
                                          )}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography
                                          variant="body2"
                                          sx={{ fontSize: "0.75rem" }}
                                        >
                                          {(
                                            scenario.additionalRepairs || 0
                                          ).toLocaleString()}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="center">
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() => {
                                            setSelectedScenario(scenario);
                                            setSelectedBucket(bucket);
                                            setCalculationDialogOpen(true);
                                          }}
                                        >
                                          <CalculateIcon fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  );
                                }
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )
            )}
          </Box>

          {/* Key Insights */}
          <Box sx={{ mt: 3 }}>
            <Card sx={{ backgroundColor: "#f9fafb" }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  💡 Key Insights
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Targeted Offers Win
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Offering discounts only to customers who decline
                        preserves margins on natural acceptances while capturing
                        additional conversions.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Price Elasticity Matters
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        The relationship between price changes and acceptance
                        rate changes is critical. Adjust the elasticity slider
                        to see different scenarios.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Price Sensitivity Varies by Range
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Different price ranges show different sensitivities. Use
                        the bucket analysis to see optimal strategies for each
                        price range.
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        </>
      )}

      {/* Calculation Details Dialog */}
      <Dialog
        open={calculationDialogOpen}
        onClose={() => setCalculationDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h6">Calculation Details</Typography>
            {selectedScenario && (
              <Typography variant="subtitle2" color="text.secondary">
                {selectedScenario.scenario}
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setCalculationDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedScenario && selectedJobCodeData && (
            <Box>
              {/* Use bucket data if available, otherwise use overall job code data */}
              {(() => {
                const dataSource = selectedBucket || selectedJobCodeData;
                const avgPrice = 'avgQuotePrice' in dataSource ? dataSource.avgQuotePrice : dataSource.avgEstimatePrice;
                return (
                  <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {selectedScenario.description}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Input Parameters
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total D2C Orders
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {(
                      dataSource.totalD2CCompletes || 0
                    ).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Current Acceptance Rate
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatPercent(dataSource.acceptanceRate || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Avg Estimate Price
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatCurrency(avgPrice || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    D2C Est Revenue
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatCurrency(dataSource.avgRepairRevenue || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    D2C Est Profit
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatCurrency(dataSource.avgRepairProfit || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    ED Profit (Per Decline)
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatCurrency(dataSource.avgTripProfit || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Price Elasticity
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {priceElasticity.toFixed(2)}
                  </Typography>
                </Grid>
                {selectedJobCodeData.appliancePrice && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Replacement Cost
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {formatCurrency(
                          selectedJobCodeData.appliancePrice || 0
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Repair vs Replace Ratio
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color:
                            (selectedJobCodeData.repairVsReplaceRatio || 0) < 50
                              ? "success.main"
                              : (selectedJobCodeData.repairVsReplaceRatio ||
                                  0) < 80
                              ? "warning.main"
                              : "error.main",
                        }}
                      >
                        {selectedJobCodeData.repairVsReplaceRatio || 0}%
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Calculation Steps
              </Typography>
              <Box
                sx={{
                  bgcolor: "#f5f5f5",
                  p: 2,
                  borderRadius: 1,
                  fontFamily: "monospace",
                  fontSize: "0.85rem",
                }}
              >
                {selectedScenario.scenario.includes("Baseline") && (
                  <>
                    <Typography variant="body2">
                      1. Current Accepts = Total Orders × Acceptance Rate
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ ml: 2, color: "text.secondary" }}
                    >
                      = {dataSource.totalD2CCompletes} ×{" "}
                      {(dataSource.acceptanceRate / 100).toFixed(4)} ={" "}
                      {Math.round(
                        dataSource.totalD2CCompletes *
                          (dataSource.acceptanceRate / 100)
                      )}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      2. Current Declines = Total Orders - Accepts
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ ml: 2, color: "text.secondary" }}
                    >
                      = {dataSource.totalD2CCompletes} -{" "}
                      {Math.round(
                        dataSource.totalD2CCompletes *
                          (dataSource.acceptanceRate / 100)
                      )}{" "}
                      ={" "}
                      {Math.round(
                        dataSource.totalD2CCompletes *
                          (1 - dataSource.acceptanceRate / 100)
                      )}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      3. Total Profit = (Accepts × D2C Est Profit) + (Declines ×
                      ED Profit)
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ ml: 2, color: "text.secondary" }}
                    >
                      = (
                      {Math.round(
                        dataSource.totalD2CCompletes *
                          (dataSource.acceptanceRate / 100)
                      )}{" "}
                      × ${dataSource.avgRepairProfit}) + (
                      {Math.round(
                        dataSource.totalD2CCompletes *
                          (1 - dataSource.acceptanceRate / 100)
                      )}{" "}
                      × ${dataSource.avgTripProfit})
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ ml: 2, color: "text.secondary" }}
                    >
                      = {formatCurrency(selectedScenario.totalProfit)}
                    </Typography>
                  </>
                )}
                {selectedScenario.scenario.includes("Blanket") && (
                  <>
                    <Typography variant="body2">
                      1. Discount Factor ={" "}
                      {(100 - selectedScenario.avgDiscount) / 100}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      2. New Price = Current Price × Discount Factor
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ ml: 2, color: "text.secondary" }}
                    >
                      = ${avgPrice} ×{" "}
                      {(100 - selectedScenario.avgDiscount) / 100} ={" "}
                      {formatCurrency(
                        (avgPrice *
                          (100 - selectedScenario.avgDiscount)) /
                          100
                      )}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      3. Price Change % = (New Price - Old Price) / Old Price
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ ml: 2, color: "text.secondary" }}
                    >
                      = {((selectedScenario.avgDiscount / 100) * -1).toFixed(4)}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      4. New Acceptance Rate = Old Rate × (1 + (Price Change % ×
                      Elasticity))
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ ml: 2, color: "text.secondary" }}
                    >
                      = {formatPercent(selectedScenario.acceptanceRate)}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      5. New D2C Est Profit = Old Profit × Discount Factor
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ ml: 2, color: "text.secondary" }}
                    >
                      = ${dataSource.avgRepairProfit} ×{" "}
                      {(100 - selectedScenario.avgDiscount) / 100} ={" "}
                      {formatCurrency(
                        (dataSource.avgRepairProfit *
                          (100 - selectedScenario.avgDiscount)) /
                          100
                      )}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      6. Total Profit = (New Accepts × New Profit) + (New
                      Declines × ED Profit)
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ ml: 2, color: "text.secondary" }}
                    >
                      = {formatCurrency(selectedScenario.totalProfit)}
                    </Typography>
                  </>
                )}
                {selectedScenario.scenario.includes("Targeted") && (
                  <>
                    <Typography variant="body2">
                      1. Original Accepts ={" "}
                      {Math.round(
                        dataSource.totalD2CCompletes *
                          (dataSource.acceptanceRate / 100)
                      )}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      2. Original Declines ={" "}
                      {Math.round(
                        dataSource.totalD2CCompletes *
                          (1 - dataSource.acceptanceRate / 100)
                      )}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      3. Conversion Rate = min(0.4, |Price Change % × Elasticity
                      × 0.5|)
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ ml: 2, color: "text.secondary" }}
                    >
                      Estimated conversion of declines to accepts
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      4. Converted Accepts = Original Declines × Conversion Rate
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ ml: 2, color: "text.secondary" }}
                    >
                      = {selectedScenario.additionalRepairs} additional repairs
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      5. Offer Profit = D2C Est Profit - (D2C Est Revenue ×
                      Discount%)
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ ml: 2, color: "text.secondary" }}
                    >
                      = ${dataSource.avgRepairProfit} - ($
                      {dataSource.avgRepairRevenue} ×{" "}
                      {selectedScenario.avgDiscount / 100}) ={" "}
                      {formatCurrency(
                        dataSource.avgRepairProfit -
                          (dataSource.avgRepairRevenue *
                            selectedScenario.avgDiscount) /
                            100
                      )}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      6. Total Profit = (Original Accepts × Full Profit) +
                      (Converted × Offer Profit) + (Remaining Declines × ED
                      Profit)
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ ml: 2, color: "text.secondary" }}
                    >
                      = {formatCurrency(selectedScenario.totalProfit)}
                    </Typography>
                  </>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Results Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total Profit
                  </Typography>
                  <Typography variant="h6" sx={{ color: "success.main" }}>
                    {formatCurrency(selectedScenario.totalProfit)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Profit Change
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color:
                        selectedScenario.profitDelta > 0
                          ? "success.main"
                          : "error.main",
                    }}
                  >
                    {selectedScenario.profitDelta > 0 ? "+" : ""}
                    {formatCurrency(selectedScenario.profitDelta)} (
                    {selectedScenario.profitDelta > 0 ? "+" : ""}
                    {formatPercent(selectedScenario.profitDeltaPct)})
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    New Acceptance Rate
                  </Typography>
                  <Typography variant="h6">
                    {formatPercent(selectedScenario.acceptanceRate)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Additional Repairs
                  </Typography>
                  <Typography variant="h6">
                    {selectedScenario.additionalRepairs.toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
                  </>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCalculationDialogOpen(false)}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

EstimateOptimization.displayName = "EstimateOptimization";

export default EstimateOptimization;
